import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { submitOrderStatus, flushQueue, onQueueChange } from "@/lib/offlineQueue";
import { useSocket } from "@/lib/socket";
import { useFeatures, FeatureOff } from "@/lib/features";
import { useAuth } from "@/context/AuthContext";
import { playChime, unlockAudio, playAlarm, isAudioUnlocked, subscribeAudio, playOrderChime, playReadyChime, playRequestChime, playBillChime } from "@/lib/chime";
import OrdersBoard from "@/components/dashboard/OrdersBoard";
import TablesGrid from "@/components/dashboard/TablesGrid";
import PaymentsPanel from "@/components/dashboard/PaymentsPanel";
import WaiterCalls from "@/components/dashboard/WaiterCalls";
import AddOrderDialog from "@/components/dashboard/AddOrderDialog";
import ChangeRequestsPanel from "@/components/dashboard/ChangeRequestsPanel";
import { StockAlertBanner, useStockAlerts } from "@/components/common/StockAlertBanner";
import { LayoutGrid, ClipboardList, CreditCard, HandPlatter, Volume2, VolumeX, Plus } from "lucide-react";
import SyncStatus from "@/components/SyncStatus";
import NotifBadge from "@/components/common/NotifBadge";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { StationSwitcher, useCompactHeader } from "@/components/common/StationSwitcher";
import { allowedStations, defaultStation, STATIONS } from "@/lib/stations";
import { acceptEvent } from "@/lib/notify";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useWebPush } from "@/hooks/useWebPush";

const NAV = [
  { key: "tables", label: "Tables", icon: LayoutGrid },
  { key: "orders", label: "Commandes", icon: ClipboardList },
  { key: "payments", label: "Encaissement", icon: CreditCard },
  { key: "calls", label: "Appels", icon: HandPlatter },
];

export default function POSPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const compact = useCompactHeader();
  // Garde RBAC côté UI (défense en profondeur) : un rôle sans accès POS est redirigé vers son poste par défaut.
  useEffect(() => {
    if (user && !allowedStations(user).includes("pos")) navigate(STATIONS[defaultStation(user)].route, { replace: true });
  }, [user, navigate]);
  const [readyMin, setReadyMin] = useState(3);
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const [tablesStatus, setTablesStatus] = useState("loading"); // loading | ok | forbidden | error
  const [slug, setSlug] = useState(null);
  const [tab, setTab] = useState("tables");
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [changeReqs, setChangeReqs] = useState([]);
  const loadChangeReqs = () => slug && api.listChangeRequests(slug).then(setChangeReqs).catch(() => {});
  const decideChange = async (cr, accept) => {
    try { const r = await api.decideChangeRequest(slug, cr.id, accept); setChangeReqs((p) => p.filter((x) => x.id !== cr.id)); loadOrders();
      toast.success(accept ? `Retrait accepté · ${cr.order_number}${r.credit ? ` · avoir ${eur(r.credit.amount)} (commande déjà payée)` : ""}` : `Retrait refusé · ${cr.order_number}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action impossible"); }
  };
  const [soundOn, setSoundOn] = useState(true);
  const [audioReady, setAudioReady] = useState(isAudioUnlocked());
  useEffect(() => subscribeAudio(setAudioReady), []);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [payFocus, setPayFocus] = useState(null);
  const [linkQR, setLinkQR] = useState(null);
  const soundRef = useRef(true); soundRef.current = soundOn;

  // Restaurant actif = celui de l'utilisateur (isolation). Jamais null : repli sur le restaurant
  // de démonstration pour l'admin/propriétaire sans restaurant_slug → les tables se chargent toujours.
  useEffect(() => { setSlug(user?.restaurant_slug || "comptoir-parisien"); }, [user]);
  useEffect(() => { if (slug) api.getRestaurant(slug).then((r) => setReadyMin(r?.ready_alert_minutes ?? 3)).catch(() => {}); }, [slug]);

  const loadTables = () => {
    if (!slug) return;
    return api.getTables(slug)
      .then((d) => { setTables(d); setTablesStatus("ok"); })
      .catch((e) => setTablesStatus(e?.response?.status === 403 ? "forbidden" : "error"));
  };
  // Cause racine "affiché mais muet" : les données récupérées par le polling/resync (et non par le
  // socket) mettaient à jour l'écran sans passer par les handlers de notification. On rejoue ici
  // les mêmes alertes (son + toast) — acceptEvent garantit zéro doublon si le socket a déjà alerté.
  const primedRef = useRef({ orders: false, requests: false });
  const loadOrders = () => slug && api.listOrders(slug).then((list) => {
    setOrders(list);
    if (!primedRef.current.orders) { primedRef.current.orders = true; return; }
    list.forEach((o) => {
      if (o.status === "NOUVELLE") notifyNewOrder(o);
      else if (o.status === "PRETE") notifyReady(o);
    });
  }).catch(() => {});
  const loadPayments = () => slug && api.listPayments(slug).then(setPayments).catch(() => {});
  const loadOverview = () => slug && api.paymentsOverview(slug).then(setOverview).catch(() => {});
  const loadRequests = () => slug && api.listServiceRequests(slug).then((list) => {
    setRequests(list);
    if (!primedRef.current.requests) { primedRef.current.requests = true; return; }
    list.filter((r) => !r.handled).forEach(notifyRequest);
  }).catch(() => {});
  const refreshPay = () => { loadPayments(); loadOverview(); loadTables(); };

  const notifyNewOrder = (o) => {
    if (!acceptEvent(`order:${o.id}:${o.status}`)) return;
    if (o.source === "CLIENT_QR") {
      if (soundRef.current) playOrderChime();
      const count = (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0);
      toast(`🍽️ NOUVELLE COMMANDE — Table ${o.table_number}`, {
        id: `order-${o.id}`, duration: Infinity,
        description: `${o.order_code || o.order_number} · ${eur(o.total)} · ${count} article${count > 1 ? "s" : ""}`,
        action: { label: "Accepter", onClick: () => handleStatus(o.id, "EN_PREPARATION") },
        cancel: { label: "Voir", onClick: () => setTab("orders") },
      });
    } else if (soundRef.current) {
      playChime();
    }
  };
  const notifyReady = (o) => {
    if (!acceptEvent(`order:${o.id}:PRETE`)) return;
    if (soundRef.current) playReadyChime();
    toast.success(`🔔 COMMANDE PRÊTE — Table ${o.table_number}`, {
      id: `ready-${o.id}`, duration: Infinity,
      description: `${o.order_code || o.order_number} · prête, à servir`,
      action: { label: "Marquer servie", onClick: () => handleStatus(o.id, "SERVIE") },
      cancel: { label: "Voir", onClick: () => setTab("orders") },
    });
  };
  const notifyRequest = (r) => {
    if (!acceptEvent(`sr:${r.id}`)) return;
    if (soundRef.current) playRequestChime(r.type);
    toast.warning(`🙋 Table ${r.table_number} — ${r.label || "Demande client"}`);
  };

  // Chargement initial (démarrage / F5 / changement de restaurant). Le temps réel passe
  // ensuite exclusivement par les événements WebSocket ci-dessous.
  useWakeLock();
  useWebPush(slug);
  useEffect(() => {
    if (!slug) return;
    loadTables(); loadOrders(); loadPayments(); loadOverview(); loadRequests(); loadChangeReqs();
    flushQueue(loadOrders);
    // eslint-disable-next-line
  }, [slug]);

  // Charge l'aperçu paiements à l'ouverture de l'onglet Encaissement.
  useEffect(() => { if (tab === "payments") { loadPayments(); loadOverview(); } /* eslint-disable-next-line */ }, [tab]);

  const [queued, setQueued] = useState(0);

  // Renvoie/régénère un lien de paiement Stripe pour la table (résolution rapide d'un échec).
  const resendPaymentLink = async (table) => {
    try {
      const r = await api.createCheckout({ restaurant_slug: slug, table_number: String(table), origin_url: window.location.origin });
      const url = r?.url || r?.checkout_url;
      if (url) { setLinkQR({ url, table }); toast.success(`Lien de paiement régénéré · Table ${table}`); }
      else toast.error("Lien de paiement indisponible");
    } catch (e) { toast.error("Impossible de générer le lien de paiement"); }
  };
  useEffect(() => onQueueChange(setQueued), []);
  const stock = useStockAlerts(slug, soundRef);
  const feats = useFeatures(slug);

  const { connected } = useSocket(slug ? [`resto:${slug}`] : [], {
    ...stock.handlers,
    features_updated: feats.reload,
    new_order: (o) => {
      setOrders((p) => p.find((x) => x.id === o.id) ? p : [o, ...p]);
      setNewOrderIds((p) => new Set(p).add(o.id));
      loadTables();
      notifyNewOrder(o);
    },
    order_updated: (o) => {
      setOrders((p) => p.map((x) => (x.id === o.id ? o : x)));
      loadTables();
      // Prise en charge/temps réel : la notif "nouvelle" disparaît sur TOUS les postes dès l'acceptation.
      if (o.status !== "NOUVELLE") {
        toast.dismiss(`order-${o.id}`);
        setNewOrderIds((p) => { const n = new Set(p); n.delete(o.id); return n; });
      }
      // Retour cuisine -> salle : notification "commande prête" persistante + son, visible hors onglet.
      if (o.status === "PRETE") notifyReady(o);
      if (o.status === "SERVIE" || o.status === "REFUSEE") toast.dismiss(`ready-${o.id}`);
    },
    service_request: (r) => { setRequests((p) => p.find((x) => x.id === r.id) ? p : [r, ...p]); notifyRequest(r); },
    change_request: (cr) => {
      setChangeReqs((p) => p.find((x) => x.id === cr.id) ? p : [cr, ...p]);
      if (soundRef.current) playRequestChime("change");
      toast(`Table ${cr.table_number} · demande de modification`, { id: `cr-${cr.id}`, description: `${cr.order_number} · ${cr.changes.map((c) => `${c.name} ×${c.before}→${c.after}`).join(", ")}`, duration: 15000 });
    },
    change_request_decided: (cr) => { setChangeReqs((p) => p.filter((x) => x.id !== cr.id)); toast.dismiss(`cr-${cr.id}`); },
    service_request_updated: () => loadRequests(),
    payment_request: (p) => { refreshPay(); if (soundRef.current) playBillChime(); toast.warning(`💳 Table ${p?.table_number ?? ""} — Demande d'encaissement`); },
    payment_settled: () => refreshPay(),
    payment_failed: (p) => {
      refreshPay();
      if (!acceptEvent(p.event_id || `payfail:${p?.table_number}`)) return;
      if (soundRef.current) playAlarm();
      toast.error(`⛔ PAIEMENT ÉCHOUÉ — Table ${p.table_number}`, {
        id: `payfail-${p.table_number}`, duration: Infinity,
        description: (
          <div className="mt-1">
            <p className="text-sm">Reste à régler : <b>{eur(p.remaining)}</b>{p.detail ? ` · ${p.detail}` : ""}</p>
            <div className="flex gap-2 mt-2">
              <button data-testid="payfail-cash" onClick={() => { setTab("payments"); setPayFocus({ table: p.table_number, remaining: p.remaining, nonce: Date.now() }); toast.dismiss(`payfail-${p.table_number}`); }}
                className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] text-xs font-bold px-3 py-1.5">Encaisser espèces</button>
              <button data-testid="payfail-link" onClick={() => { resendPaymentLink(p.table_number); toast.dismiss(`payfail-${p.table_number}`); }}
                className="rounded-full bg-[#1A3D32] hover:bg-[#123026] text-stone-100 text-xs font-semibold px-3 py-1.5 border border-[#2C5446]">Renvoyer le lien</button>
            </div>
          </div>
        ),
      });
    },
    order_reminder: (o) => {
      if (!acceptEvent(`remind:${o.id}:${o.reminder_count}`)) return;
      if (soundRef.current) playChime();
      toast.warning(`⏰ TOUJOURS EN ATTENTE — Table ${o.table_number}`, {
        id: `ready-${o.id}`, duration: Infinity,
        description: `${o.order_code || o.order_number} · prête, à servir (relance ${o.reminder_count})`,
        action: { label: "Marquer servie", onClick: () => handleStatus(o.id, "SERVIE") },
        cancel: { label: "Voir", onClick: () => setTab("orders") },
      });
    },
    resync: () => { flushQueue(() => { loadOrders(); loadTables(); }); loadOrders(); loadTables(); loadRequests(); if (tab === "payments") loadOverview(); },
  });

  // Filet de réconciliation : recharge périodique INDÉPENDANTE de l'état "connected"
  // (un socket "zombie" garde connected=true sans recevoir les events). Garantit la fraîcheur sans F5.
  useEffect(() => {
    if (!slug) return;
    const id = setInterval(() => { loadTables(); loadOrders(); loadRequests(); if (tab === "payments") loadOverview(); }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [slug, tab]);

  const handleStatus = async (id, status) => {
    setOrders((p) => p.map((o) => (o.id === id ? { ...o, status } : o)));
    try {
      const res = await submitOrderStatus(id, status);
      if (res.order) setOrders((p) => p.map((o) => (o.id === id ? res.order : o)));
      loadTables();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); loadOrders(); }
  };
  const lastReadyAlertRef = useRef(0);
  const itemStatus = async (id, status, idx) => {
    try { const o = await api.updateItemStatus(id, status, idx); setOrders((p) => p.map((x) => (x.id === id ? o : x))); loadTables(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); loadOrders(); }
  };
  const takeService = async (id) => {
    setOrders((p) => p.map((o) => (o.id === id ? { ...o, taken_for_service_at: new Date().toISOString(), taken_for_service_by: user?.name || user?.email } : o)));
    try { const o = await api.takeOrderService(id); setOrders((p) => p.map((x) => (x.id === id ? o : x))); } catch (e) { toast.error("Erreur"); loadOrders(); }
  };
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      const stale = readyMin > 0 && orders.some((o) => o.status === "PRETE" && !o.taken_for_service_at && o.ready_at && now - new Date(o.ready_at).getTime() > readyMin * 60000);
      if (stale && soundRef.current && now - lastReadyAlertRef.current > 30000) {
        lastReadyAlertRef.current = now;
        playChime();
        if (navigator.vibrate) { try { navigator.vibrate([200, 100, 200]); } catch (e) {} }
      }
    }, 15000);
    return () => clearInterval(t);
  }, [orders, readyMin]);
  const handleClaim = async (id) => {
    try { const r = await api.claimServiceRequest(id); setRequests((p) => p.map((x) => (x.id === id ? r : x))); toast.success("Pris en charge"); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        if (d?.request) setRequests((p) => p.map((x) => (x.id === id ? d.request : x)));
        toast.info(`Déjà prise en charge par ${d?.claimed_by || "un collègue"}`);
      } else { toast.error("Erreur"); }
    }
  };
  const handleDone = async (id) => { try { await api.handleServiceRequest(id); setRequests((p) => p.filter((x) => x.id !== id)); loadTables(); toast.success("Terminé"); } catch (e) { toast.error("Erreur"); } };
  const enableSound = () => { unlockAudio(); setAudioReady(true); playChime(); };

  if (!slug) return <div className="min-h-screen bg-[#0C1713] grid place-items-center"><div className="w-8 h-8 rounded-full border-2 border-[#C69C4A] border-t-transparent animate-spin" /></div>;

  const callCount = requests.length + changeReqs.length;
  const payCount = payments.filter((p) => p.method === "cash" && p.payment_status === "pending").length;
  const newCount = orders.filter((o) => o.status === "NOUVELLE").length;

  if (!feats.has("pos")) return <div className="min-h-screen bg-[#0C1713] p-6"><FeatureOff label="Caisse POS" /></div>;
  return (
    <div className="dark-scroll min-h-screen bg-[#0C1713] text-stone-100 flex flex-col" style={{ overflowX: "clip" }}>
      <header className={`sticky top-0 z-20 bg-[#08110E] border-b border-[#1B3028] px-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 transition-[padding] duration-200 ${compact ? "py-1.5" : "py-3"}`}>
        <div className="flex items-center gap-2.5 shrink-0 order-1">
          <div className={`rounded-lg bg-[#C69C4A] text-[#0C1713] grid place-items-center font-serif-display font-bold transition-all ${compact ? "w-8 h-8 text-base" : "w-9 h-9 text-lg"}`}>N</div>
          <div>
            <p className="font-serif-display text-lg leading-none">Caisse / POS</p>
            <p className="text-[10px] uppercase tracking-widest text-[#C69C4A]"><SyncStatus connected={connected} queued={queued} /></p>
            <NotifBadge slug={slug} />
          </div>
        </div>
        <nav className="flex gap-1.5 overflow-x-auto no-scrollbar scroll-fade-x order-3 basis-full w-full sm:order-2 sm:basis-auto sm:w-auto sm:flex-1 sm:min-w-0">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`pos-nav-${n.key}`} onClick={() => setTab(n.key)}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${tab === n.key ? "bg-[#1A3D32] text-white" : "text-stone-400 hover:text-stone-100"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
              {n.key === "orders" && newCount > 0 && <span data-testid="pos-new-order-badge" className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">{newCount}</span>}
              {n.key === "calls" && callCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">{callCount}</span>}
              {n.key === "payments" && payCount > 0 && <span className="bg-[#C69C4A] text-[#0C1713] text-[10px] font-bold rounded-full px-1.5">{payCount}</span>}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0 order-2 sm:order-3">
          <button data-testid="pos-new-order" onClick={() => setAddOpen(true)} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold text-xs px-3 py-2 flex items-center gap-1.5"><Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nouvelle commande</span></button>
          {!audioReady && <button data-testid="pos-enable-sound" onClick={enableSound} className="rounded-full bg-[#1A3D32] text-stone-100 font-semibold text-xs px-3 py-2">Activer le son</button>}
          <button data-testid="pos-sound-toggle" onClick={() => setSoundOn((s) => !s)} className={`w-9 h-9 rounded-full grid place-items-center ${soundOn ? "bg-[#1A3D32] text-[#C69C4A]" : "bg-[#111F1A] text-stone-500"}`}>{soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
          <StationSwitcher current="pos" slug={slug} />
        </div>
      </header>

      <StockAlertBanner alerts={stock.alerts} onDismiss={stock.dismiss} />
      <main className="flex-1 p-4 lg:p-6">
        {tab === "tables" && (
          tablesStatus === "loading" ? <p data-testid="tables-loading" className="text-sm text-stone-500">Chargement des tables…</p>
          : tablesStatus === "forbidden" ? <p data-testid="tables-forbidden" className="text-sm text-stone-500">Accès aux tables non autorisé pour ce rôle.</p>
          : tablesStatus === "error" ? <p data-testid="tables-error" className="text-sm text-amber-400">Impossible de charger les tables (réseau/serveur). Nouvelle tentative automatique…</p>
          : <TablesGrid tables={tables} slug={slug} onRefresh={loadTables} />
        )}
        {tab === "orders" && <OrdersBoard orders={orders} onStatus={handleStatus} newOrderIds={newOrderIds} slug={slug} payments={payments} onTakeService={takeService} readyAlertMin={readyMin} onItemStatus={itemStatus} />}
        {tab === "payments" && <PaymentsPanel slug={slug} payments={payments} overview={overview} onRefresh={refreshPay} isAdmin={isAdmin} focusTable={payFocus?.table} focusRemaining={payFocus?.remaining} focusNonce={payFocus?.nonce} />}

      <Dialog open={!!linkQR} onOpenChange={(o) => { if (!o) setLinkQR(null); }}>
        <DialogContent data-testid="paylink-qr-dialog" className="max-w-xs rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          <DialogTitle className="text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#C69C4A]" /> Paiement · Table {linkQR?.table}</DialogTitle>
          <p className="text-xs text-stone-400 -mt-1">Présentez ce QR code au client pour qu'il règle sa table.</p>
          <div className="flex justify-center bg-white rounded-2xl p-4 my-2">
            {linkQR && <QRCodeCanvas value={linkQR.url} size={200} fgColor="#123026" level="M" />}
          </div>
          <div className="flex gap-2">
            <button data-testid="paylink-open" onClick={() => window.open(linkQR.url, "_blank")} className="flex-1 h-11 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] text-sm font-bold">Ouvrir</button>
            <button data-testid="paylink-copy" onClick={() => { navigator.clipboard?.writeText(linkQR.url); toast.success("Lien copié"); }} className="flex-1 h-11 rounded-full border border-[#1B3028] text-stone-200 text-sm font-semibold">Copier</button>
          </div>
        </DialogContent>
      </Dialog>
        {tab === "calls" && <><ChangeRequestsPanel requests={changeReqs} onDecide={decideChange} /><WaiterCalls requests={requests} onClaim={handleClaim} onHandle={handleDone} /></>}
      </main>
      <AddOrderDialog open={addOpen} onClose={() => setAddOpen(false)} slug={slug} tables={tables} onDone={() => { loadOrders(); loadTables(); }} />
    </div>
  );
}
